import type { CategoryFieldOption, CategorySection, FormTypeOption } from "./types"

export const YES_NO: CategoryFieldOption[] = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
]

export const FORM_TYPE_OPTIONS: FormTypeOption[] = [
  { value: "Commercial Office Space (Vacant)", label: "Commercial Office Space (Vacant)", category: "Commercial", status: "Vacant" },
  { value: "Commercial Office Space (Pre-Rented)", label: "Commercial Office Space (Pre-Rented)", category: "Commercial", status: "Pre-Rented" },
  { value: "Independent Building (Commercial office - Vacant)", label: "Independent Building (Commercial office - Vacant)", category: "Commercial", status: "Vacant" },
  { value: "Independent Building (Commercial office - Pre-Rented)", label: "Independent Building (Commercial office - Pre-Rented)", category: "Commercial", status: "Pre-Rented" },
  { value: "Independent Building (Industrial - Vacant)", label: "Independent Building (Industrial - Vacant)", category: "Industrial", status: "Vacant" },
  { value: "Independent Building (Industrial - Pre-Rented)", label: "Independent Building (Industrial - Pre-Rented)", category: "Industrial", status: "Pre-Rented" },
  { value: "Independent Building (Retail - Vacant)", label: "Independent Building (Retail - Vacant)", category: "Commercial", status: "Vacant" },
  { value: "Independent Building (Retail - Pre-Rented)", label: "Independent Building (Retail - Pre-Rented)", category: "Commercial", status: "Pre-Rented" },
  { value: "Retail Space (Vacant)", label: "Retail Space (Vacant)", category: "Commercial", status: "Vacant" },
  { value: "Retail Space (Pre-Rented)", label: "Retail Space (Pre-Rented)", category: "Commercial", status: "Pre-Rented" },
  { value: "Warehouse (Vacant)", label: "Warehouse (Vacant)", category: "Industrial", status: "Vacant" },
  { value: "Warehouse (Pre-Rented)", label: "Warehouse (Pre-Rented)", category: "Industrial", status: "Pre-Rented" },
  { value: "Hospitality (Operational)", label: "Hospitality (Operational)", category: "Commercial", status: "Operational" },
  { value: "Hospitality (Vacant or Non-Operational)", label: "Hospitality (Vacant or Non-Operational)", category: "Commercial", status: "Vacant or Non-Operational" },
  { value: "Franchise", label: "Franchise", category: "Commercial", status: "" },
  { value: "Land", label: "Land", category: "Land", status: "" },
  { value: "Admin Form", label: "Admin Form", category: "Admin", status: "" },
]

export const CATEGORIES_WITHOUT_OCCUPANCY = ["Land", "Franchise", "Admin Form"]

export const CATEGORY_OPTIONS = [
  { value: "Commercial Office Space", label: "Commercial Office Space" },
  { value: "Independent Building (Commercial office)", label: "Independent Building (Commercial office)" },
  { value: "Independent Building (Industrial)", label: "Independent Building (Industrial)" },
  { value: "Independent Building (Retail)", label: "Independent Building (Retail)" },
  { value: "Retail Space", label: "Retail Space" },
  // { value: "Warehouse", label: "Warehouse" },
  // { value: "Hospitality", label: "Hospitality" },
  // { value: "Franchise", label: "Franchise" },
  // { value: "Land", label: "Land" },
  { value: "Admin Form", label: "Admin Form" },
]

const ADMIN_FORM_ROLES = ["Super-admin", "Admin"]

export function canShowAdminForm(role: string | null | undefined): boolean {
  if (!role) return false
  return ADMIN_FORM_ROLES.some((r) => r.trim().toLowerCase() === role.trim().toLowerCase())
}

export const COMMON_ADMIN_SECTIONS: CategorySection[] = [

  {
    sectionTitle: "Admin Verification & Enrichment",
    fields: [
      { key: "admin_yearBuiltRenovated", type: "text", label: "Year Built / Renovated", placeholder: "" },
      { key: "admin_addTags", type: "text", label: "Add Tags", placeholder: "" },
    ],
  },
  {
    sectionTitle: "Bidding & Auction Parameters",
    fields: [
      { key: "__reservePrice__", type: "number", label: "Reserve Price (₹)", placeholder: "e.g. 50,00,000", tooltip: "This serves as the public lowest bid amount for the auction." },
      { key: "__biddingStartPrice__", type: "number", label: "Starting Bid Amount (₹)", placeholder: "e.g. 50,00,000", tooltip: "CRITICAL: Enter the opening bid amount." },
      { key: "__biddingMinIncrement__", type: "number", label: "Bid Increment (₹)", placeholder: "e.g. 1,00,000", tooltip: "Enter the minimum amount by which a subsequent bid must be raised. E.g., '1,00,000'." },
      { key: "__auctionStartDate__", type: "datetime", label: "Auction Start Date & Time", placeholder: "Select date & time", tooltip: "Set the exact date and time the auction will officially begin and bidding can start." },
      { key: "__auctionEndDate__", type: "datetime", label: "Auction End Date & Time", placeholder: "Select date & time", tooltip: "Set the exact date and time the auction will close." },
    ],
  },
  {
    sectionTitle: "Analytics / Investment Highlights",
    fields: [
      { key: "admin_ribbonText", type: "text", label: "Ribbon Text", placeholder: "" },
    ],
  },
  {
    sectionTitle: "Media & Data Room",
    fields: [
      { key: "admin_propertyVideoUrl", type: "text", label: "Property Video URL", placeholder: "e.g. https://youtube.com/... or https://vimeo.com/...", tooltip: "Paste the primary YouTube or Vimeo link for the property video tour." },
      { key: "admin_matterport3dTourUrl", type: "text", label: "Matterport / 3D Tour URL", placeholder: "e.g. https://my.matterport.com/...", tooltip: "Paste the link for the immersive 3D walkthrough." },
    ],
  },
  {
    sectionTitle: "Status & Internal Management",
    fields: [
      { key: "admin_status", type: "text", label: "Status", placeholder: "Autopick from auction dates", tooltip: "Control the public state of the listing: Upcoming (Visible but not open for bidding), Live (Bidding is active), Inactive (Hidden from public view), Sold/Deal Closed." },
      { key: "admin_isExclusiveMandate", type: "yesno", label: "Exclusive mandate with SoldX", options: YES_NO, placeholder: "Select", tooltip: "Exclusive: Mark if this is an exclusive mandate with SoldX." },
      { key: "admin_canBrokerBid", type: "yesno", label: "Can Broker Bid", options: YES_NO, placeholder: "Select", tooltip: "Allow registered brokers to place bids, potentially on behalf of clients." },
      { key: "admin_assignToAuctionAdvisor", type: "dropdown", label: "Assign to Auction Advisor", placeholder: "Select broker", tooltip: "Assign an internal team member from the list to be the auction advisor for this listing." },
    ],
  },
]

export const STATUS_VACANT_PRE_RENTED: CategoryFieldOption[] = [
  { value: "Vacant", label: "Vacant" },
  { value: "Pre-Rented", label: "Pre-Rented" },
]

export const STATUS_VACANT_RENTED: CategoryFieldOption[] = [
  { value: "Vacant", label: "Vacant" },
  { value: "Rented", label: "Rented" },
]

export const STATUS_HOSPITALITY: CategoryFieldOption[] = [
  { value: "Operational", label: "Operational" },
  { value: "Vacant or Non-Operational", label: "Vacant or Non-Operational" },
]
